/**
 * Per-day stacked bar chart for mastery distribution (m1-m5).
 * m0 is excluded — it's the seeded curriculum pool, not learning signal.
 */
import type { DailyStatsRow } from "../../api";

const COLORS = ["#e0b98e", "#d08949", "#a25618", "#6b3108", "#3a1a03"];
// index 0 = m1, index 4 = m5

export default function MasteryStackedBars({
  title,
  rows,
}: {
  title: string;
  rows: DailyStatsRow[];
}) {
  const W = 640;
  const H = 220;
  const PAD = { t: 20, r: 20, b: 30, l: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const n = rows.length;

  if (n === 0) {
    return (
      <div className="card">
        <div className="mb-3">
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">{title}</div>
          <div className="text-[11px] text-ink-faint mt-0.5">
            m1–m5, seeded pool excluded
          </div>
        </div>
        <div className="text-ink-faint text-sm py-8 text-center">no data yet</div>
      </div>
    );
  }

  const seenMax = Math.max(...rows.map((r) => r.seen || 0), 1);
  const barW = Math.max(3, innerW / n - 1);

  return (
    <div className="card">
      <div className="mb-3">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">{title}</div>
        <div className="text-[11px] text-ink-faint mt-0.5">
          m1–m5, seeded pool excluded
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <g stroke="#e4e1db" strokeWidth="1">
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={i}
              x1={PAD.l}
              y1={PAD.t + (innerH * i) / 4}
              x2={W - PAD.r}
              y2={PAD.t + (innerH * i) / 4}
            />
          ))}
        </g>
        <g fill="#6d6a63" fontSize="10" textAnchor="end">
          {[0, 1, 2, 3, 4].map((i) => {
            const y = PAD.t + (innerH * i) / 4;
            const val = Math.round(seenMax * (1 - i / 4));
            return (
              <text key={i} x={PAD.l - 4} y={y + 3}>
                {val}
              </text>
            );
          })}
        </g>
        {rows.map((row, i) => {
          const x =
            PAD.l +
            (n > 1 ? (i * innerW) / (n - 1) : innerW / 2) -
            barW / 2;
          let yCursor = PAD.t + innerH;
          const dist = row.mastery_dist || {};
          const stacks = [];
          for (let m = 1; m <= 5; m++) {
            const count = Number(dist[String(m)] ?? 0);
            if (!count) continue;
            const h = (innerH * count) / seenMax;
            yCursor -= h;
            stacks.push(
              <rect
                key={m}
                x={x}
                y={yCursor}
                width={barW}
                height={h}
                fill={COLORS[m - 1]}
              />,
            );
          }
          return <g key={i}>{stacks}</g>;
        })}
        <g fill="#6d6a63" fontSize="10" textAnchor="middle">
          <text x={PAD.l} y={H - 8}>
            d1
          </text>
          <text x={W - PAD.r} y={H - 8}>
            d{n}
          </text>
        </g>
      </svg>
      <div className="mt-2 text-[11px] text-ink-soft flex flex-wrap gap-3">
        {[1, 2, 3, 4, 5].map((m) => (
          <span key={m} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: COLORS[m - 1] }}
            />
            m{m}
          </span>
        ))}
      </div>
    </div>
  );
}
